/**
 * editorSections.ts — collapse state for the Edit Resume lightbox.
 *
 * Every card in the editor body is an accordion. Which ones start open is a
 * DERIVED default (some depend on the section's generation type), so the only
 * thing persisted is the set of blocks the user has explicitly toggled — an
 * "overrides" map. A block the user has never touched always follows the
 * current default, which is why flipping Awards to Omit collapses it and
 * flipping it back re-opens it.
 *
 * Keys are the editor's NAV KEYS (`data-nav-key`), not section components, so
 * the fixed blocks (name, resume URLs, synonyms, …) share one map with the nine
 * draggable resume sections. `basics` is the historical key of the block that
 * now holds only the professional Name — the key is kept so help-target and
 * scrollspy wiring stay stable.
 */
import type { GenerationType, SubcomponentType } from '../types';

/** localStorage key for the accordion overrides (per user, not per resume). */
export const OPEN_SECTIONS_KEY = 'ss_edit_open_sections';

/** Nav key of the block holding the professional name (legacy id: `basics`). */
export const NAME_SECTION_KEY = 'basics';

/** Nav key of the Resume URLs block. */
export const RESUME_URLS_KEY = 'resumeurls';

/**
 * Fixed blocks that render BELOW the reorderable sections, in body order, after
 * the visual divider. (Cover Letter is fixed too but sits above the divider.)
 */
export const EDITOR_TAIL_KEYS: string[] = [
  RESUME_URLS_KEY, 'synonyms', 'writingsample', 'impliedfacts', 'demographics',
];

/**
 * Blocks that start COLLAPSED: reference/setup material a user visits once,
 * not the content they edit every session.
 */
export const DEFAULT_CLOSED_KEYS = new Set<string>(EDITOR_TAIL_KEYS);

/**
 * Sections whose default is conditional: they hold real content, so they open —
 * unless the user has set them to Omit, in which case they are not going on any
 * resume and only take up room.
 */
export const OMIT_COLLAPSES_KEYS = new Set<SubcomponentType>([
  'awards', 'presentations', 'publications', 'portfolios',
]);

/** Whether `key` starts open for a resume with these generation types. */
export function defaultSectionOpen(
  key: string,
  genTypes?: Partial<Record<SubcomponentType, GenerationType>>,
): boolean {
  if (DEFAULT_CLOSED_KEYS.has(key)) return false;
  if (OMIT_COLLAPSES_KEYS.has(key as SubcomponentType)) {
    return (genTypes || {})[key as SubcomponentType] !== 'omit';
  }
  return true;
}

/** Coerce anything read back from localStorage into an overrides map. */
export function sanitizeOpenSections(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean' && k) out[k] = v;
  }
  return out;
}

/** Resolved open/closed state: an explicit user toggle beats the default. */
export function isSectionOpen(
  key: string,
  overrides: Record<string, boolean>,
  genTypes?: Partial<Record<SubcomponentType, GenerationType>>,
): boolean {
  const stored = overrides[key];
  return typeof stored === 'boolean' ? stored : defaultSectionOpen(key, genTypes);
}

/** Read the persisted overrides; a corrupt/absent value means "no overrides". */
export function readOpenSections(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(OPEN_SECTIONS_KEY);
    return sanitizeOpenSections(stored ? JSON.parse(stored) : null);
  } catch {
    return {};
  }
}

/** Persist the overrides map (best-effort — storage may be unavailable). */
export function writeOpenSections(next: Record<string, boolean>): void {
  try {
    localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(next));
  } catch { /* non-fatal */ }
}
